// src/constants/notificationRouting.js
// PHASE 1.4 VERIFIER:   r1-a48f1c93
// PHASE 1.6.1 VERIFIER: r1-161-web-routing-4f1c8d2a
//
// Single source-of-truth deep-link routing map for in-app notifications.
//
// USED BY: ProtectedCrmLayout.handleNotificationClick
//
// Returns a URL path string to navigate to, or null if the notification
// should be marked-read but produce no navigation (e.g. note_reminder
// in the Phase 1.4 transitional state — its proper destination is the
// flagged-note inbox shipping in Phase 1.6).
//
// CrmApp.js already handles /quotes/:id, /invoices/:id, /jobs/:id deep
// links via its handleDeepLinks effect. Customer detail routing is
// added in this phase via the same pattern. So returning a URL path
// from this map is sufficient — no additional state plumbing required.
//
// To add a new notification type:
//   1. Add a case to the switch in getNotificationRoute below.
//   2. Add the same type to PUSH_TYPE_TITLES in functions/utils/notificationBell.js
//      (qnotif-6d2f8b47) — it is the push title map AND the push allowlist;
//      a type missing there lands in the bell but never pushes.
//   3. Add a matching entry to spatchy-mobile/src/constants/notificationRouting.js
//      so push-tap and in-app deep-link both work on mobile.

/**
 * Resolves a notification doc to a URL path string.
 *
 * @param {object} notification — the notification doc, including .type,
 *   .entityId, and (for note_flag) .targetEntityType + .targetEntityId.
 * @returns {string|null} URL path to navigate to, or null for no-nav.
 */
export const getNotificationRoute = (notification) => {
    if (!notification || !notification.type) return null;

    const { type, entityId, targetEntityType, targetEntityId } = notification;

    switch (type) {
        // ─── Jobs ─────────────────────────────────────────────────────────
        case 'tech_flag':
        case 'job_reminder':
            return entityId ? `/jobs/${entityId}` : null;

        // ─── Assignment (calpol-9c4e7b23) ─────────────────────────────────
        // The server has sent both types since notifyAssign shipped
        // (functions/index.js:5111, :5118) and already puts the JOB id in
        // `entityId` — the same field job_reminder above consumes — so routing
        // these needed NO backend change. They simply had no case, fell to
        // `default: return null` below, and so marked themselves read and
        // navigated nowhere. Mobile closed its half in mobnest-3c7f9a25
        // (2026-09-28); this is the web half, and the two now agree.
        case 'job_assigned':
            return entityId ? `/jobs/${entityId}` : null;

        // job_unassigned deliberately does NOT open the job, matching the
        // founder decision taken for mobile. firestore.rules:730 lets any org
        // member READ any job, so opening it would have worked — but the job is
        // no longer theirs, and putting it on screen invites them to act on it
        // or drive to it. The message already names the job that was taken off
        // them; the useful next question is what IS theirs, so this lands on
        // the jobs list. Returns a path unconditionally because the destination
        // does not depend on entityId.
        case 'job_unassigned':
            return '/jobs';

        // ─── Notes (Phase 1.1 / 1.3 / 1.6.1) ──────────────────────────────
        // PHASE 1.6.1 VERIFIER: r1-161-web-routing-4f1c8d2a
        // Server hydrates targetEntityType + targetEntityId on the notif doc
        // at write time (onNoteCreated / reassignNoteFlag in functions/index.js).
        // Priority resolved server-side: job > quote > invoice > customer.
        //
        // 1.6.1: ?tab=notesMedia appended to all four destinations so the
        // landing surface opens on the Notes & Media tab where the flagged
        // note actually sits. Today only CustomerFormModal honors this tab
        // (CustomerProfileScreen on mobile too); Job/Quote/Invoice surfaces
        // get the param ignored until Phase 1.18 ships their Notes & Media
        // tab and adds an initialTab prop. The routing layer is already
        // correct then — no notificationRouting changes needed at 1.18.
        case 'note_flag': {
            if (!targetEntityId) return null; // attachedTo[] was empty — no nav
            switch (targetEntityType) {
                case 'job':      return `/jobs/${targetEntityId}?tab=notesMedia`;
                case 'quote':    return `/quotes/${targetEntityId}?tab=notesMedia`;
                case 'invoice':  return `/invoices/${targetEntityId}?tab=notesMedia`;
                case 'customer': return `/customers/${targetEntityId}?tab=notesMedia`;
                default:         return null;
            }
        }

        // ─── Note reminder (Phase 1.1 / 1.6) ──────────────────────────────
        // PHASE 1.6 VERIFIER: r1-16-web-routing-16a3f2b8
        // Daily summary of unresolved flagged notes from dailyFlaggedNoteReminder.
        // Phase 1.6 ships the triage inbox at /flagged, which is the proper
        // destination. Pre-1.6 this returned null (no-op) — the comment block
        // explicitly anticipated this update.
        case 'note_reminder':
            return '/flagged';

        // ─── Quotes ───────────────────────────────────────────────────────
        // quote_viewed — Seam 11b (qnotif-6d2f8b47): the customer opened this
        // estimate; entityId is the QUOTE id (recordQuoteView → createNotification).
        case 'quote_approved':
        case 'quote_declined':
        case 'quote_changes':
        case 'quote_response':
        case 'quote_follow_up':
        case 'quote_viewed':
            return entityId ? `/quotes/${entityId}` : null;

        // Seam 11c (qnotif-6d2f8b47): the daily digest. entityId is the ORG id
        // (a digest is about many estimates), so it never opens one — it lands
        // on the Estimates list with Needs attention switched on. `attention`
        // is its own param, NOT a ?filter= value: filter is the status axis and
        // attention ANDs with it (QuotesView applies it once, then strips it).
        case 'quote_digest':
            return '/quotes?attention=1';

        // ─── Invoices ─────────────────────────────────────────────────────
        // DEPOSITS SEAM 4 VERIFIER: r2-dep-carrythrough-5b8d2e91
        // 'deposit_remainder' — a paid deposit was larger than the first
        // invoice it landed on. entityId is that INVOICE (not the estimate):
        // the invoice is where the owner sees what was applied, and the
        // leftover is parked on the estimate as deposit.unappliedRemainder
        // for them to apply by hand.
        // s0bcharge-bc5e9925 / s0bunrec-1ef97499 — S0b office bells (invoice_balance_review,
        // payment_unrecorded); entityId is the INVOICE.
        case 'invoice_paid':
        case 'deposit_remainder':
        case 'invoice_balance_review':
        case 'payment_unrecorded':
            return entityId ? `/invoices/${entityId}` : null;

        // tzcanada-9b4e2f17 — 'account_payment' (Seam 11 fireAccountPaymentBell in functions/index.js:
        // a Pay All charge that didn't record, recovered as account credit — "review the customer's
        // account"). entityId is the CUSTOMER. It had no route, so the bell only marked itself read.
        case 'account_payment':
            return entityId ? `/customers/${entityId}` : null;

        // ─── Leads / requests ─────────────────────────────────────────────
        // 'new_lead' = submitted via spatchy.co/book/:slug public form.
        // 'new_request' = submitted via authenticated customer portal.
        // Both land on the same leads view; entityId is the request doc id.
        case 'new_lead':
        case 'new_request':
            return entityId ? `/leads/${entityId}` : '/leads';

        // ─── SOPs (sopremind-9c4e7a21, Seam 4) ────────────────────────────
        // entityId is the SOP. Staff have no per-SOP page, so every SOP bell
        // opens My SOPs with that SOP selected (MySops reads ?open=). The
        // phone's half is SOPs Seam 6.
        case 'sop_reminder':
        case 'sop_overdue':
        case 'sop_new_version':
        case 'sop_reread':
            return entityId ? `/sops?open=${encodeURIComponent(entityId)}` : '/sops';

        // ─── Unknown type — no nav, mark read only ────────────────────────
        default:
            return null;
    }
};

// ─── getNoteAttachmentRoute (Phase 1.6 / 1.6.1) ────────────────────────────
// PHASE 1.6 VERIFIER:   r1-16-web-routing-16a3f2b8
// PHASE 1.6.1 VERIFIER: r1-161-web-routing-4f1c8d2a
//
// Resolves a NOTE doc (not a notification doc) to a URL path string, using
// the same job > quote > invoice > customer priority that onNoteCreated
// applies server-side when hydrating notification.targetEntityType.
//
// Used by the Phase 1.6 Flagged Inbox row's Open button. The inbox queries
// notes directly, so the row doesn't have a notification doc to call
// getNotificationRoute with — it has the underlying note. Rather than
// fabricating a notification-shaped object on the row, this helper does
// the priority resolution against attachedTo[] directly.
//
// 1.6.1: ?tab=notesMedia appended to all four destinations so Flagged
// Inbox Open opens on the Notes & Media tab. Mirrors the note_flag case
// in getNotificationRoute above so bell-click and Inbox-Open produce the
// same URL for a given note. See that block for the Phase 1.18 caveat.
//
// Returns null when:
//   - note is missing
//   - attachedTo is empty or not an array
//   - no entry matches one of the four known types
//
// @param {object} note — a note doc with .attachedTo: Array<{type, id}>
// @returns {string|null} URL path, or null if nothing to navigate to.
export const getNoteAttachmentRoute = (note) => {
    if (!note || !Array.isArray(note.attachedTo) || note.attachedTo.length === 0) {
        return null;
    }
    const priority = ['job', 'quote', 'invoice', 'customer'];
    for (const type of priority) {
        const match = note.attachedTo.find(
            (a) => a && a.type === type && typeof a.id === 'string' && a.id.length > 0,
        );
        if (!match) continue;
        switch (type) {
            case 'job':      return `/jobs/${match.id}?tab=notesMedia`;
            case 'quote':    return `/quotes/${match.id}?tab=notesMedia`;
            case 'invoice':  return `/invoices/${match.id}?tab=notesMedia`;
            case 'customer': return `/customers/${match.id}?tab=notesMedia`;
            default:         return null;
        }
    }
    return null;
};